import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MenuBar from "@/components/MenuBar";
import DemographicInformation from "./employee/DemographicInformation";
import JobInformation from "./employee/JobInformation";
import TaxInformation from "./employee/TaxInformation";
import { useToast } from "@/components/ui/use-toast";
import {
  EmployeeDemographicInfo,
  EmployeeTaxInfo,
  EmployeeJobInfo,
} from "./types/employeeTypes";
import ImportInformation from "@/components/ImportInformation";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import EmployeeInvites from "@/components/EmployeeInvites";
import { useAuth } from "@/hooks/useAuth";
import { isNannyUser, isSuperUser } from "@/types/user";
import { Menu, Loader2 } from "lucide-react";
import { TerminationProvider } from "@/contexts/TerminationContext";
import { JobQuickActions } from "@/components/employee/job";

export type EmployeeTab = "demographic" | "job" | "tax" | "imports" | "invites";

export interface EmployeeOverviewResponse {
  demographicInfo: EmployeeDemographicInfo;
  taxInfo: EmployeeTaxInfo | null;
  jobInfo: EmployeeJobInfo | null;
  hasFilingBeenDone: boolean;
  switchingPayrollProviders: boolean;
}

const EmployeeOverview: React.FC = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNanny = user ? isNannyUser(user) : false;
  const isSuperUserAccount = isSuperUser(user);
  const [selectedUserId, setSelectedUserId] = useState<string>("current");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [haveFilingsBeenDone, setHaveFilingsBeenDone] =
    useState<boolean>(false);
  const [switchingPayrollProviders, setSwitchingPayrollProviders] =
    useState<boolean>(false);
  const [demographicInfo, setDemographicInfo] =
    useState<EmployeeDemographicInfo | null>(null);
  const [taxInfo, setTaxInfo] = useState<EmployeeTaxInfo | null>(null);
  const [jobInfo, setJobInfo] = useState<EmployeeJobInfo | null>(null);
  const [showAdvancedJobSettings, setShowAdvancedJobSettings] = useState(false);

  const currentTab = (searchParams.get("tab") as EmployeeTab) || "demographic";

  useEffect(() => {
    const userIdParam = searchParams.get('userId');
    if (isSuperUserAccount && userIdParam) {
      setSelectedUserId(userIdParam);
    }
  }, [searchParams, isSuperUserAccount]);

  useEffect(() => {
    fetchEmployeeData();
  }, [employeeId, selectedUserId]);

  const fetchEmployeeData = async () => {
    try {
      setIsLoading(true);
      if (!employeeId) return;

      const data = await ApiService.request<EmployeeOverviewResponse>(
        EndpointKeys.GET_EMPLOYEE_DETAILS,
        {
          urlParams: { id: employeeId },
          ...(isSuperUserAccount && selectedUserId !== "current" && {
            queryParams: { userId: selectedUserId }
          })
        },
      );

      setDemographicInfo(data.demographicInfo);
      if (data.taxInfo && Object.keys(data.taxInfo).length > 0) {
        setTaxInfo(data.taxInfo);
      }
      if (data.jobInfo) {
        setJobInfo(data.jobInfo);
      }
      setHaveFilingsBeenDone(data.hasFilingBeenDone);
      setSwitchingPayrollProviders(data.switchingPayrollProviders);
    } catch (error) {
      console.error("Error fetching employee data:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch employee data",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleJobCreated = () => {
    fetchEmployeeData();
  };

  const handleTabChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", value);
    
    if (isSuperUserAccount && selectedUserId !== "current") {
      newParams.set("userId", selectedUserId);
    }
    
    setSearchParams(newParams);
  };

  useEffect(() => {
    if (isLoading) return;

    if (
      isNanny &&
      (currentTab === "job" ||
        currentTab === "imports" ||
        currentTab === "invites")
    ) {
      setSearchParams({ tab: "demographic" });
    } else if (currentTab === "tax" && (!jobInfo || !jobInfo.job)) {
      setSearchParams({ tab: "demographic" });
    }
  }, [currentTab, isNanny, jobInfo, isLoading]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 md:ml-0 overflow-hidden mt-16 md:mt-0">
        <div className="p-4 md:p-8 overflow-y-auto h-full">
          <div className="max-w-7xl space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 md:mb-8">
            <h1 className="text-2xl md:text-3xl font-semibold">
              {isNanny ? "My Profile" : "Employee Overview"}
            </h1>
            {!isNanny && <Button>Edit Employee</Button>}
          </div>

          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                <h2 className="text-xl md:text-2xl font-semibold">
                  {demographicInfo
                    ? `${demographicInfo.firstName} ${demographicInfo.lastName}`
                    : "Loading..."}
                </h2>
                {haveFilingsBeenDone && (
                  <div className="text-sm text-yellow-600 bg-yellow-50 px-3 py-1 rounded-full self-start md:self-auto">
                    Read-only: Payrun has been processed
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Tabs
                value={currentTab}
                onValueChange={handleTabChange}
                className="space-y-4"
              >
                <div className="overflow-x-auto">
                  <TabsList className="mb-4 w-auto inline-flex">
                    <TabsTrigger value="demographic">
                      Demographic Information
                    </TabsTrigger>
                    {!isNanny && (
                      <TabsTrigger value="job">Job Information</TabsTrigger>
                    )}
                    {jobInfo && jobInfo.job && (
                      <TabsTrigger value="tax">Tax Information</TabsTrigger>
                    )}
                    {!isNanny && employeeId && (
                      <TabsTrigger value="imports">
                        Previous Payroll
                      </TabsTrigger>
                    )}
                    {!isNanny && employeeId && (
                      <TabsTrigger value="invites">
                        Employee Invites
                      </TabsTrigger>
                    )}
                  </TabsList>
                </div>

                <div className="mt-4">
                  <TabsContent value="demographic">
                    <DemographicInformation
                      demographicInfo={demographicInfo}
                      setDemographicInfo={setDemographicInfo}
                      employeeId={employeeId}
                      toast={toast}
                      readOnly={haveFilingsBeenDone}
                    />
                  </TabsContent>

                  {!isNanny && (
                    <TabsContent value="job">
                      <TerminationProvider>
                        <div className="space-y-6">
                          {/* New Job Quick Actions Component */}
                          <JobQuickActions
                            employeeId={parseInt(employeeId!)}
                            showAdvanced={showAdvancedJobSettings}
                            onToggleAdvanced={() => setShowAdvancedJobSettings(!showAdvancedJobSettings)}
                            onSetupComplete={() => {
                              // Refresh employee data when setup is complete
                              fetchEmployeeData();
                              toast({
                                title: "Job Setup Complete!",
                                description: "The employee's job has been set up successfully.",
                              });
                            }}
                          />
                          
                          {/* Advanced Job Information - only show when advanced settings are enabled */}
                          {showAdvancedJobSettings && (
                            <JobInformation
                              employeeId={employeeId}
                              onJobCreated={handleJobCreated}
                            />
                          )}
                        </div>
                      </TerminationProvider>
                    </TabsContent>
                  )}

                  {jobInfo && jobInfo.job && (
                    <TabsContent value="tax">
                      <TaxInformation
                        taxInfo={taxInfo}
                        setTaxInfo={setTaxInfo}
                        employeeId={employeeId}
                        toast={toast}
                        hasFilingBeenDone={haveFilingsBeenDone}
                        readOnly={haveFilingsBeenDone}
                        switchingPayrollProviders={switchingPayrollProviders}
                      />
                    </TabsContent>
                  )}

                  {!isNanny && employeeId && (
                    <TabsContent value="imports">
                      <ImportInformation employeeId={employeeId} />
                    </TabsContent>
                  )}

                  {!isNanny && employeeId && (
                    <TabsContent value="invites">
                      <EmployeeInvites employeeId={employeeId} />
                    </TabsContent>
                  )}
                </div>
              </Tabs>
            </CardContent>
          </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeOverview;
